import React, { ChangeEvent, useEffect } from 'react';
import { noop } from 'lodash';
import { Stack } from '@fiverr-private/layout_components';
import {
  Form,
  FormControl,
  FormElement,
  FormHeader,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputPrefix,
  useForm,
} from '@fiverr-private/forms';
import { getCurrencySymbol } from '@fiverr-private/localization';
import { getContext } from '@fiverr-private/fiverr_context';
import { AppI18n } from '../../../../i18n';
import { useBriefCreationContext } from '../../../../context/BriefCreationContext';
import { briefCreationDrawerEducationIsShown } from '../../../../monitoring/bqEvents';
import { getCentsInCurrency } from '../utils/formatCurrency';
import { useBudgetAndTimelineContext } from '../utils/context';
import {
  BudgetAndTimelineContent,
  FIXED_PRICE_PLACEHOLDER,
  getCommaFormatedStringAsNumber,
  numberRegex,
} from '../utils';
import { placeholderStyle } from '../utils/styles.ve.css';

interface FixPriceInputProps {
  validateAndChange: (state: BudgetAndTimelineContent) => Promise<void>;
  methods: ReturnType<typeof useForm<{ budget: string }>>;
  maxWidth: React.ComponentProps<typeof InputGroup>['maxWidth'];
}

export const FixPriceInput = ({ validateAndChange, methods, maxWidth }: FixPriceInputProps) => {
  const { currency, briefClassification, contextId, source, variant } = useBriefCreationContext();
  const { locale } = getContext();
  const { setSectionContentState, sectionContentState } = useBudgetAndTimelineContext();

  const currencySymbol = getCurrencySymbol({ currencyCode: currency.name, formattingLocale: locale });

  const leafClassification = briefClassification?.leaf_classification[0];

  useEffect(
    () => {
      // fire budget error BQ event
      if (methods?.formState?.errors?.budget) {
        briefCreationDrawerEducationIsShown({
          educationType: 'invalid_budget',
          categoryId: leafClassification?.category_id,
          subCategoryId: leafClassification?.sub_category_id,
          nestedCategoryId: leafClassification?.nested_sub_category_id,
          briefContextId: contextId,
          eventSource: source || '',
          variant,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods?.formState?.errors?.budget]
  );

  const handleBudgetChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const valueAsNumber = getCommaFormatedStringAsNumber(value);
    if (!numberRegex.test(valueAsNumber.toString())) {
      return;
    }

    const updatedValue = { ...sectionContentState, budgetInCents: (valueAsNumber / currency.rate) * 100 };
    setSectionContentState(updatedValue);
    await validateAndChange(updatedValue);
  };

  return (
    <Stack direction="column" gap="1.5">
      <Form methods={methods} onSubmit={noop}>
        <FormControl name="budget">
          <FormHeader>
            <FormLabel>
              <AppI18n k="sections.budget.up_to" />
            </FormLabel>
          </FormHeader>
          <FormElement>
            <InputGroup
              maxWidth={maxWidth}
              height="42px"
              onChange={handleBudgetChange}
              value={
                sectionContentState.budgetInCents
                  ? getCentsInCurrency(sectionContentState.budgetInCents, currency, true)
                  : ''
              }
            >
              <Input placeholder={FIXED_PRICE_PLACEHOLDER} className={placeholderStyle} autoFocus />
              <InputPrefix>{currencySymbol}</InputPrefix>
            </InputGroup>
          </FormElement>
          <FormHelperText error={methods.formState.errors.budget} />
        </FormControl>
      </Form>
    </Stack>
  );
};
